import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UserPen } from "lucide-react";

import {
  fetchMyArtistProfile,
  updateMyArtistProfile,
  fetchMyArtworks,
  uploadMyAvatarImage,
  uploadMyBannerImage,
  uploadMyProfileBgImage,
} from "../../services/arthouseApi";

import ShopLinksPanel from "../../components/ArtHouse/ShopLinksPanel";
import CreatorTopBar from "../../components/ArtHouse/CreatorTopBar";

import { useArtists } from "../../context/ArtistContext";

const HAS_PROFILE_KEY = "ARTHOUSE_HAS_PROFILE";
const MY_ARTIST_ID_KEY = "ARTHOUSE_MY_ARTIST_ID";

const EMPTY_ARTIST = {
  display_name: "",
  bio: "",
  avatar_url: "",
  banner_url: "",
  profile_bg_url: "",
  instagram_url: "",
  twitter_url: "",
  website_url: "",
  store_printify_url: "",
  store_etsy_url: "",
  store_shopify_url: "",
  store_gumroad_url: "",
};

function getArtistId(a) {
  return a?.id ?? a?.artist_id ?? null;
}

function isProbablyUrl(v) {
  if (!v) return true;
  try {
    new URL(v);
    return true;
  } catch {
    return false;
  }
}

function validateImageFile(file, { maxMB = 6 } = {}) {
  if (!file) return "No file selected.";
  const okTypes = ["image/png", "image/jpeg", "image/webp"];
  if (!okTypes.includes(file.type)) return "Only PNG/JPG/WebP allowed.";
  if (file.size > maxMB * 1024 * 1024) return `Image too large (max ${maxMB}MB).`;
  return "";
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

function getArtworkImage(a) {
  return (
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    ""
  );
}

function safeText(v, fallback = "") {
  return (v ?? "").toString().trim() || fallback;
}

function safeUrl(u) {
  const s = String(u || "").trim();
  return s && (s.startsWith("http://") || s.startsWith("https://")) ? s : "";
}

/** NEW (only used for the card click): snapshot passed to ArtistMiniCardModal */
function getArtworkSnapshot(a) {
  if (!a) return null;

  const image_url =
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    "";

  return {
    id: a?.id ?? a?.artwork_id ?? a?.artworkId ?? null,
    title: a?.title || "Untitled",
    description: a?.description || a?.caption || a?.subtitle || "",
    image_url,
    tags: Array.isArray(a?.tags) ? a.tags : [],
    merchLinks: Array.isArray(a?.merchLinks) ? a.merchLinks : [],
    views: a?.views,
    likes: a?.likes,
    artist: a?.artist || a?.creator || null,
  };
}

function IconLink({ href, label, children }) {
  const url = safeUrl(href);
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="
        inline-flex items-center gap-2
        rounded-full border border-white/10
        bg-white/[0.06] px-3 py-2
        text-xs font-semibold text-white/80
        hover:bg-white/[0.10] hover:border-white/20
        transition
      "
      aria-label={label}
      title={label}
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </a>
  );
}

function TinyPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3">
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold text-white/90">{value}</div>
    </div>
  );
}

function ProfileTabs({ tab, setTab, counts }) {
  const item =
    "h-10 px-4 rounded-xl text-sm font-semibold border transition inline-flex items-center justify-center";
  const on =
    "border-sky-300/25 bg-sky-300/10 text-sky-200 shadow-[0_0_0_1px_rgba(125,211,252,0.14)]";
  const off =
    "border-white/10 bg-white/[0.04] text-white/70 hover:bg-white/[0.08] hover:border-white/15";

  return (
    <div className="mt-5 flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => setTab("gallery")}
        className={`${item} ${tab === "gallery" ? on : off}`}
      >
        Gallery <span className="ml-2 text-xs text-white/45">({counts.posts})</span>
      </button>

      <button
        type="button"
        onClick={() => setTab("about")}
        className={`${item} ${tab === "about" ? on : off}`}
      >
        About
      </button>

      <button
        type="button"
        onClick={() => setTab("links")}
        className={`${item} ${tab === "links" ? on : off}`}
      >
        Links
      </button>
    </div>
  );
}

function ArtworkIGGrid({ artworks = [], hubState, artistId }) {
  // ONLY CHANGE: clicking a card opens /arthouse/artist-modal/:artistId (like your gallery does)
  const location = useLocation();
  const navigate = useNavigate();

  function openMiniCard(artwork) {
    if (!artistId) return;

    navigate(`/arthouse/artist-modal/${artistId}`, {
      state: {
        backgroundLocation: location,
        ...(hubState || {}),
        artwork: getArtworkSnapshot(artwork),
      },
    });
  }

  return (
    <div
      className="
        mt-6
        grid gap-2 sm:gap-3
        grid-cols-2 md:grid-cols-3 xl:grid-cols-4
      "
    >
      {artworks.map((a) => {
        const img = getArtworkImage(a);
        const title = safeText(a?.title, "Untitled");
        const id = a?.id ?? a?.artwork_id;

        return (
          <button
            key={String(id ?? title) + String(img)}
            type="button"
            onClick={() => openMiniCard(a)}
            className="
              group relative overflow-hidden w-full text-left
              rounded-2xl border border-white/10 bg-white/[0.03]
              hover:bg-white/[0.06] hover:border-white/15
              transition
            "
            title={title}
          >
            {/* square */}
            <div className="relative aspect-square bg-black/30">
              {img ? (
                <>
                  <img
                    src={img}
                    alt={title}
                    className="
                      absolute inset-0 h-full w-full object-cover
                      opacity-[0.96]
                      group-hover:scale-[1.02]
                      transition duration-300
                    "
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition" />
                </>
              ) : (
                <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_35%_0%,rgba(56,189,248,0.14),rgba(0,0,0,0)_60%),radial-gradient(900px_480px_at_100%_20%,rgba(125,211,252,0.10),rgba(0,0,0,0)_55%)]" />
              )}

              {/* hover overlay */}
              <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-sm font-semibold text-white/95 line-clamp-1">
                    {title}
                  </div>
                  <div className="mt-1 text-[11px] text-white/60">
                    View artwork →
                  </div>
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function MyArtistProfilePage() {
  const location = useLocation();

  // Preserve hub navigation state (so returning doesn't snap Hub back to Community)
  const hubState = useMemo(() => {
    const incoming = location.state || {};
    return {
      ...incoming,
      hubSection: incoming.hubSection || "arthouse",
    };
  }, [location.state]);

  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("gallery");

  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // avatar upload state
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState("");

  // banner upload state
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerMsg, setBannerMsg] = useState("");

  // profile bg upload state
  const [bgUploading, setBgUploading] = useState(false);
  const [bgMsg, setBgMsg] = useState("");

  const { upsertArtist, getArtist } = useArtists();

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const profile = await fetchMyArtistProfile();
        const myArt = await fetchMyArtworks();
        if (!alive) return;

        const a = profile?.artist || null;

        if (a) {
          setArtist(a);
          setArtworks(myArt?.artworks || []);

          const id = getArtistId(a);
          if (id) {
            localStorage.setItem(HAS_PROFILE_KEY, "1");
            localStorage.setItem(MY_ARTIST_ID_KEY, String(id));
            upsertArtist(a);
          }

          setEditing(false);
          setTab("gallery");
          return;
        }

        localStorage.removeItem(HAS_PROFILE_KEY);
        localStorage.removeItem(MY_ARTIST_ID_KEY);

        setArtist({ ...EMPTY_ARTIST });
        setArtworks([]);
        setEditing(true);
        setTab("about");
      } catch (e) {
        if (!alive) return;

        const msg = String(e?.message || "").toLowerCase();
        const isNotFound = e?.status === 404 || msg.includes("artist not found");

        if (isNotFound) {
          localStorage.removeItem(HAS_PROFILE_KEY);
          localStorage.removeItem(MY_ARTIST_ID_KEY);

          setArtist({ ...EMPTY_ARTIST });
          setArtworks([]);
          setEditing(true);
          setTab("about");
        } else {
          setErrorMsg(e?.message || "Failed to load your artist profile.");
          setEditing(false);
        }
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [upsertArtist]);

  const liveArtist = useMemo(() => {
    const id = getArtistId(artist);
    if (!id) return artist;
    return getArtist(id) || artist;
  }, [artist, getArtist]);

  const normalizedArtworks = useMemo(() => {
    if (!liveArtist) return [];
    return (artworks || []).map((a) => ({
      ...a,
      artist: (a?.artist?.id && (getArtist(a.artist.id) || a.artist)) || liveArtist,
    }));
  }, [artworks, liveArtist, getArtist]);

  const counts = useMemo(() => {
    const posts = (normalizedArtworks || []).length;
    return { posts };
  }, [normalizedArtworks]);

  async function saveProfile() {
    if (!artist) return;

    setSaving(true);
    setSavedFlash(false);
    setErrorMsg("");

    if (!isProbablyUrl(artist.banner_url)) {
      setSaving(false);
      setErrorMsg("Banner URL is not a valid URL.");
      return;
    }
    if (!isProbablyUrl(artist.profile_bg_url)) {
      setSaving(false);
      setErrorMsg("Profile background URL is not a valid URL.");
      return;
    }

    try {
      const updated = await updateMyArtistProfile(artist);
      const a = updated?.artist || null;

      setArtist(a);

      const id = getArtistId(a);
      if (id) {
        localStorage.setItem(HAS_PROFILE_KEY, "1");
        localStorage.setItem(MY_ARTIST_ID_KEY, String(id));
        upsertArtist(a);
      }

      try {
        const myArt = await fetchMyArtworks();
        setArtworks(myArt?.artworks || []);
      } catch {}

      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1400);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  }

  async function onPickAvatar(file) {
    setAvatarMsg("");
    setErrorMsg("");

    const v = validateImageFile(file, { maxMB: 3 });
    if (v) {
      setAvatarMsg(v);
      return;
    }

    setAvatarUploading(true);
    try {
      const res = await uploadMyAvatarImage(file);

      const nextUrl =
        res?.avatarUrl || res?.artist?.avatar_url || res?.artist?.avatarUrl || null;

      if (!nextUrl) throw new Error("Upload succeeded but no avatarUrl returned.");

      setArtist((prev) => ({
        ...(prev || {}),
        ...(res.artist || {}),
        avatar_url: nextUrl,
      }));

      if (res?.artist) upsertArtist(res.artist);

      setAvatarMsg("Avatar updated ");
      setTimeout(() => setAvatarMsg(""), 1200);
    } catch (e) {
      const msg =
        e?.data?.error || e?.data?.message || e?.message || "Failed to upload avatar.";
      setAvatarMsg(msg);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function onPickBanner(file) {
    setBannerMsg("");
    setErrorMsg("");

    const v = validateImageFile(file, { maxMB: 6 });
    if (v) {
      setBannerMsg(v);
      return;
    }

    setBannerUploading(true);
    try {
      const res = await uploadMyBannerImage(file);

      const nextUrl =
        res?.bannerUrl || res?.artist?.banner_url || res?.artist?.bannerUrl || null;

      if (!nextUrl) throw new Error("Upload succeeded but no bannerUrl returned.");

      setArtist((prev) => ({
        ...(prev || {}),
        ...(res.artist || {}),
        banner_url: nextUrl,
      }));

      if (res?.artist) upsertArtist(res.artist);

      setBannerMsg("Banner updated ");
      setTimeout(() => setBannerMsg(""), 1200);
    } catch (e) {
      const msg =
        e?.data?.error || e?.data?.message || e?.message || "Failed to upload banner.";
      setBannerMsg(msg);
    } finally {
      setBannerUploading(false);
    }
  }

  async function onPickProfileBg(file) {
    setBgMsg("");
    setErrorMsg("");

    const v = validateImageFile(file, { maxMB: 8 });
    if (v) {
      setBgMsg(v);
      return;
    }

    setBgUploading(true);
    try {
      const res = await uploadMyProfileBgImage(file);

      const nextUrl =
        res?.profileBgUrl ||
        res?.bgUrl ||
        res?.artist?.profile_bg_url ||
        res?.artist?.profileBgUrl ||
        null;

      if (!nextUrl) throw new Error("Upload succeeded but no profileBgUrl returned.");

      setArtist((prev) => ({
        ...(prev || {}),
        ...(res.artist || {}),
        profile_bg_url: nextUrl,
      }));

      if (res?.artist) upsertArtist(res.artist);

      setBgMsg("Background updated ");
      setTimeout(() => setBgMsg(""), 1200);
    } catch (e) {
      const msg =
        e?.data?.error ||
        e?.data?.message ||
        e?.message ||
        "Failed to upload background.";
      setBgMsg(msg);
    } finally {
      setBgUploading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070B16] text-white">
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 pb-14">
          <div className="rounded-[26px] border border-white/12 bg-white/[0.04] p-6 text-white/70">
            Loading…
          </div>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-[#070B16] text-white">
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 pb-14">
          <div className="rounded-[26px] border border-white/12 bg-white/[0.04] p-6">
            <div className="text-lg font-semibold text-white/90">
              Couldn’t load your profile
            </div>
            <div className="mt-2 text-sm text-white/55">
              {errorMsg || "Unknown error."}
            </div>
            <div className="mt-5">
              <Link
                to="/hub"
                state={hubState}
                className="
                  h-10 px-4 rounded-xl
                  border border-white/12 bg-white/[0.04]
                  hover:bg-white/[0.08] transition
                  text-sm font-semibold text-white/75
                  inline-flex items-center justify-center
                "
              >
                ← Back to Gallery
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const avatarUrl = liveArtist?.avatar_url || "";
  const pageBg = liveArtist?.profile_bg_url || "";
  const bannerUrl = liveArtist?.banner_url || "";

  const displayName = safeText(liveArtist?.display_name, "Artist");
  const bio = safeText(liveArtist?.bio, "");

  const social = {
    ig: safeText(liveArtist?.instagram_url),
    tw: safeText(liveArtist?.twitter_url),
    web: safeText(liveArtist?.website_url),
  };

  // NEW (for the grid click): the artistId the modal route needs
  const myArtistId = String(getArtistId(liveArtist) || "");

  return (
    <div className="min-h-screen bg-[#070B16] text-white relative">

      {/* background layer */}
      <div className="pointer-events-none fixed inset-0">
        {pageBg ? (
          <>
            <img
              src={pageBg}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-[0.26]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/70 to-[#070B16]/96" />
            <div className="absolute inset-0 [background:radial-gradient(1200px_520px_at_30%_10%,rgba(255,255,255,0.10),transparent_55%)] opacity-[0.30]" />
          </>
        ) : null}

        <div className="absolute -top-40 left-20 h-[520px] w-[520px] rounded-full bg-cyan-400/10 blur-[140px]" />
        <div className="absolute top-28 -right-44 h-[640px] w-[640px] rounded-full bg-sky-400/10 blur-[160px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.08),transparent_45%),radial-gradient(circle_at_80%_15%,rgba(125,211,252,0.06),transparent_50%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.0),rgba(7,11,22,0.8))]" />
      </div>

      <div className="relative mx-auto w-full max-w-[1200px] px-4 pt-6 pb-14">
        {/* FIX: tell CreatorTopBar to use Hub back button + state */}
        <CreatorTopBar
          hubState={hubState}
          isEditing={editing}
          onEditToggle={() => setEditing((v) => !v)}
        />

        {savedFlash && (
          <div className="mt-4 rounded-xl border border-sky-300/25 bg-sky-300/10 px-4 py-3 text-sm text-sky-200">
            Saved 
          </div>
        )}

        {errorMsg ? (
          <div className="mt-4 rounded-xl border border-white/12 bg-black/25 px-4 py-3 text-sm text-white/70">
            {errorMsg}
          </div>
        ) : null}

        {!editing ? (
          <>
            {/* Futuristic profile header */}
            <div className="mt-6 overflow-hidden rounded-[30px] border border-white/12 bg-white/[0.035] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.55)]">
              {/* banner */}
              <div className="relative h-[180px] sm:h-[220px] md:h-[260px] bg-black/25">
                {bannerUrl ? (
                  <>
                    <img
                      src={bannerUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-[0.92]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" />
                  </>
                ) : (
                  <>
                    <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_35%_0%,rgba(56,189,248,0.18),rgba(0,0,0,0)_60%),radial-gradient(900px_480px_at_100%_20%,rgba(125,211,252,0.12),rgba(0,0,0,0)_55%)]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25" />
                  </>
                )}

                {/* glass shine */}
                <div className="pointer-events-none absolute -top-20 left-1/2 h-56 w-[840px] -translate-x-1/2 rounded-full bg-white/10 blur-[40px] opacity-[0.18]" />
              </div>

              {/* body */}
              <div className="relative p-5 sm:p-6">
                <div className="flex items-start justify-between gap-5 flex-wrap">
                  <div className="flex items-start gap-4">
                    {/* avatar + profile button */}
                    <div className="-mt-14 sm:-mt-16 flex flex-col items-center gap-3">
                      <div className="h-[92px] w-[92px] sm:h-[108px] sm:w-[108px] rounded-[26px] overflow-hidden border border-white/15 bg-black/30 shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt="avatar"
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-full w-full bg-[radial-gradient(circle_at_30%_20%,rgba(56,189,248,0.20),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(125,211,252,0.12),transparent_60%)]" />
                        )}
                      </div>
                      
                      <Link
                        to="/profilehub"
                        className="
                          flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                          border border-white/12 bg-white/[0.06]
                          hover:bg-white/[0.10] hover:border-white/20
                          transition-all duration-300 ease-out
                          text-sm font-semibold text-white/85
                          hover:scale-105 active:scale-95
                        "
                      >
                        <UserPen size={16} />
                        AniProfile
                      </Link>
                    </div>

                    <div className="pt-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-xl sm:text-2xl font-semibold text-white/92">
                          {displayName}
                        </div>
                        <div className="text-[11px] tracking-[0.35em] uppercase text-white/45">
                          Artist Profile
                        </div>
                      </div>

                      {bio ? (
                        <div className="mt-2 text-sm text-white/65 leading-relaxed max-w-[62ch]">
                          {bio}
                        </div>
                      ) : (
                        <div className="mt-2 text-sm text-white/45">
                          Add a bio to tell people what you make.
                        </div>
                      )}

                      {/* social chips */}
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <IconLink href={social.ig} label="Instagram">
                          <span className="text-white/70">⌁</span>
                        </IconLink>
                        <IconLink href={social.tw} label="X / Twitter">
                          <span className="text-white/70">⌁</span>
                        </IconLink>
                        <IconLink href={social.web} label="Website">
                          <span className="text-white/70">⌁</span>
                        </IconLink>
                      </div>
                    </div>
                  </div>

                  {/* stats + actions */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <TinyPill label="Posts" value={counts.posts} />
                  </div>
                </div>

                <ProfileTabs tab={tab} setTab={setTab} counts={counts} />

                {/* tab content */}
                {tab === "gallery" ? (
                  <>
                    {/* ONLY CHANGE: cards open ArtistMiniCardModal route */}
                    <ArtworkIGGrid
                      artworks={normalizedArtworks}
                      hubState={hubState}
                      artistId={myArtistId}
                    />

                    {normalizedArtworks.length === 0 ? (
                      <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-white/60">
                        No artworks yet. Upload your first piece to start building your
                        profile.
                      </div>
                    ) : null}
                  </>
                ) : tab === "about" ? (
                  <div className="mt-6 grid gap-3">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                      <div className="text-sm font-semibold text-white/85">About</div>
                      <div className="mt-2 text-sm text-white/60 leading-relaxed">
                        {bio || "No bio yet — switch to Edit Mode to add one."}
                      </div>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <TinyPill label="Instagram" value={social.ig ? "Connected" : "—"} />
                        <TinyPill label="X / Twitter" value={social.tw ? "Connected" : "—"} />
                        <TinyPill label="Website" value={social.web ? "Connected" : "—"} />
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                      <div className="text-sm font-semibold text-white/85">
                        Store + Support
                      </div>
                      <div className="mt-2 text-sm text-white/60">
                        Add store links so people can actually buy your work.
                      </div>
                      <div className="mt-4">
                        <ShopLinksPanel artist={liveArtist} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                    <div className="text-sm font-semibold text-white/85">Links</div>
                    <div className="mt-2 text-sm text-white/60">
                      Keep your socials + stores in one place.
                    </div>
                    <div className="mt-4">
                      <ShopLinksPanel artist={liveArtist} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          // EDIT MODE (unchanged)
          <div className="mt-6 rounded-[30px] border border-white/12 bg-white/[0.035] backdrop-blur-xl overflow-hidden shadow-[0_30px_140px_rgba(0,0,0,0.55)]">
            <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-white/50">
                  Edit Mode
                </div>
                <div className="mt-1 text-lg font-semibold text-white/90">
                  Upgrade your profile
                </div>
                <div className="mt-1 text-sm text-white/55">
                  Make it clean, modern, and ready for people to follow + buy.
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="h-11 px-4 rounded-xl border border-white/12 bg-white/[0.04] hover:bg-white/[0.08] transition text-sm font-semibold text-white/80"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveProfile}
                  disabled={saving}
                  className="h-11 px-5 rounded-xl border border-sky-300/25 bg-sky-300/10 hover:bg-sky-300/15 transition text-sm font-semibold text-sky-200 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div
              className="
                p-6 grid gap-6
                [&_input]:bg-black/30
                [&_textarea]:bg-black/30
                [&_input]:border
                [&_textarea]:border
                [&_input]:border-white/15
                [&_textarea]:border-white/15
                [&_input]:rounded-2xl
                [&_textarea]:rounded-2xl
                [&_input]:px-4
                [&_textarea]:px-4
                [&_input]:py-3
                [&_textarea]:py-3
                [&_input]:text-slate-200
                [&_textarea]:text-slate-200
                [&_input::placeholder]:text-white/35
                [&_textarea::placeholder]:text-white/35
                [&_input:focus]:outline-none
                [&_textarea:focus]:outline-none
                [&_input:focus]:ring-2
                [&_textarea:focus]:ring-2
                [&_input:focus]:ring-sky-400/30
                [&_textarea:focus]:ring-sky-400/30
              "
            >
              {/* Visual customization */}
              <div className="rounded-[24px] border border-white/12 bg-black/15 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-white/85">Visuals</div>
                    <div className="mt-1 text-xs text-white/55">
                      Upload your banner + background (same style as avatar).
                    </div>
                  </div>
                  <div className="text-xs text-white/40">
                    Banner: wide • Background: portrait
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Banner upload */}
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="text-sm font-semibold text-white/85">Banner</div>
                    <div className="mt-1 text-xs text-white/55">
                      {bannerMsg ? bannerMsg : "Recommended: 1600×500+ (wide)."}
                    </div>

                    <div className="mt-3">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={bannerUploading}
                        onChange={(e) => onPickBanner(e.target.files?.[0] || null)}
                        className="block w-full text-sm text-white/70
                          file:mr-4 file:rounded-xl file:border file:border-white/12
                          file:bg-white/[0.06] file:px-4 file:py-2
                          file:text-sm file:font-semibold file:text-white/80
                          hover:file:bg-white/[0.10] file:transition
                          disabled:opacity-60"
                      />
                      <div className="mt-2 text-xs text-white/45">Max 6MB • PNG allowed</div>
                    </div>

                    <div className="mt-3">
                      <input
                        value={artist.banner_url || ""}
                        onChange={(e) =>
                          setArtist({ ...artist, banner_url: e.target.value })
                        }
                        placeholder="(Optional) Banner URL override"
                      />
                    </div>

                    <div className="mt-3 rounded-2xl overflow-hidden border border-white/10 bg-black/25">
                      <div className="relative h-28">
                        {artist.banner_url ? (
                          <>
                            <img
                              src={artist.banner_url}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover opacity-[0.92]"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/25" />
                          </>
                        ) : (
                          <div className="absolute inset-0 bg-[radial-gradient(900px_420px_at_35%_0%,rgba(56,189,248,0.14),rgba(0,0,0,0)_60%),radial-gradient(900px_480px_at_100%_20%,rgba(125,211,252,0.10),rgba(0,0,0,0)_55%)]" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Background upload */}
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="text-sm font-semibold text-white/85">
                      Profile Background
                    </div>
                    <div className="mt-1 text-xs text-white/55">
                      {bgMsg ? bgMsg : "Recommended: 1600×2000+ (portrait)."}
                    </div>

                    <div className="mt-3">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={bgUploading}
                        onChange={(e) =>
                          onPickProfileBg(e.target.files?.[0] || null)
                        }
                        className="block w-full text-sm text-white/70
                          file:mr-4 file:rounded-xl file:border file:border-white/12
                          file:bg-white/[0.06] file:px-4 file:py-2
                          file:text-sm file:font-semibold file:text-white/80
                          hover:file:bg-white/[0.10] file:transition
                          disabled:opacity-60"
                      />
                      <div className="mt-2 text-xs text-white/45">Max 8MB • PNG allowed</div>
                    </div>

                    <div className="mt-3">
                      <input
                        value={artist.profile_bg_url || ""}
                        onChange={(e) =>
                          setArtist({ ...artist, profile_bg_url: e.target.value })
                        }
                        placeholder="(Optional) Background URL override"
                      />
                    </div>

                    <div className="mt-3 rounded-2xl overflow-hidden border border-white/10 bg-black/25">
                      <div className="relative h-28">
                        {artist.profile_bg_url ? (
                          <>
                            <img
                              src={artist.profile_bg_url}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover opacity-[0.40]"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/60 to-black/80" />
                          </>
                        ) : (
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.08),transparent_45%),radial-gradient(circle_at_80%_15%,rgba(125,211,252,0.06),transparent_50%)]" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Avatar upload */}
              <div className="rounded-[24px] border border-white/12 bg-black/15 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-white/85">
                      Profile Picture
                    </div>
                    <div className="mt-1 text-xs text-white/55">
                      Upload your avatar (stored in R2).{" "}
                      {avatarMsg ? `— ${avatarMsg}` : ""}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 rounded-2xl overflow-hidden border border-white/12 bg-black/30">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt="avatar preview"
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={avatarUploading}
                    onChange={(e) => onPickAvatar(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-white/70
                      file:mr-4 file:rounded-xl file:border file:border-white/12
                      file:bg-white/[0.06] file:px-4 file:py-2
                      file:text-sm file:font-semibold file:text-white/80
                      hover:file:bg-white/[0.10] file:transition
                      disabled:opacity-60"
                  />
                  <div className="mt-2 text-xs text-white/45">Max 3MB • PNG/JPG/WebP</div>
                </div>
              </div>

              {/* Basics */}
              <div className="rounded-[24px] border border-white/12 bg-black/15 p-5">
                <div className="text-sm font-semibold text-white/85">Basics</div>
                <div className="mt-4 grid gap-3">
                  <input
                    value={artist.display_name || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, display_name: e.target.value })
                    }
                    placeholder="Display name"
                  />
                  <textarea
                    className="min-h-[140px]"
                    value={artist.bio || ""}
                    onChange={(e) => setArtist({ ...artist, bio: e.target.value })}
                    placeholder="Bio (what you make, what inspires you, commissions?)"
                  />
                </div>
              </div>

              {/* Social */}
              <div className="rounded-[24px] border border-white/12 bg-black/15 p-5">
                <div className="text-sm font-semibold text-white/85">Social</div>
                <div className="mt-4 grid gap-3">
                  <input
                    value={artist.instagram_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, instagram_url: e.target.value })
                    }
                    placeholder="Instagram URL"
                  />
                  <input
                    value={artist.twitter_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, twitter_url: e.target.value })
                    }
                    placeholder="Twitter/X URL"
                  />
                  <input
                    value={artist.website_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, website_url: e.target.value })
                    }
                    placeholder="Website URL"
                  />
                </div>
              </div>

              {/* Stores */}
              <div className="rounded-[24px] border border-white/12 bg-black/15 p-5">
                <div className="text-sm font-semibold text-white/85">Store Links</div>
                <div className="mt-4 grid gap-3">
                  <input
                    value={artist.store_printify_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, store_printify_url: e.target.value })
                    }
                    placeholder="Printify URL"
                  />
                  <input
                    value={artist.store_etsy_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, store_etsy_url: e.target.value })
                    }
                    placeholder="Etsy URL"
                  />
                  <input
                    value={artist.store_shopify_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, store_shopify_url: e.target.value })
                    }
                    placeholder="Shopify URL"
                  />
                  <input
                    value={artist.store_gumroad_url || ""}
                    onChange={(e) =>
                      setArtist({ ...artist, store_gumroad_url: e.target.value })
                    }
                    placeholder="Gumroad URL"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
